"use client";

import React from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const CustomPagination = ({ total, count }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;

  const pageCount = Math.ceil(total / count);

  const buildUrl = (pageNumber) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", pageNumber);
    return `${pathname}?${params.toString()}`;
  };

  const handlePageChange = (page) => {
    router.push(buildUrl(page));
  };

  if (pageCount <= 1) return null;

  const getPageNumbers = () => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];
    let l;

    range.push(1);
    for (let i = currentPage - delta; i <= currentPage + delta; i++) {
      if (i < pageCount && i > 1) {
        range.push(i);
      }
    }
    if (pageCount > 1) range.push(pageCount);

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  return (
    <div className="flex items-center justify-center py-4">
      <Pagination>
        <PaginationContent className="gap-1">
          {currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious
                href={buildUrl(currentPage - 1)}
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(currentPage - 1);
                }}
                className="border border-brand-light text-brand-navy hover:bg-brand-light/50 hover:text-brand-deep transition-colors rounded-lg"
              />
            </PaginationItem>
          )}

          {getPageNumbers().map((item, index) => {
            if (item === "...") {
              return (
                <PaginationItem key={`dots-${index}`}>
                  <PaginationEllipsis className="text-muted-foreground" />
                </PaginationItem>
              );
            }
            return (
              <PaginationItem key={item}>
                <PaginationLink
                  href={buildUrl(item)}
                  isActive={currentPage === item}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(item);
                  }}
                  className={
                    currentPage === item
                      ? "gradient-primary text-white border-transparent hover:opacity-95 rounded-lg shadow-brand-sm font-semibold transition-all"
                      : "border border-brand-light text-brand-navy hover:bg-brand-light/50 hover:text-brand-deep hover:border-brand-sky transition-colors rounded-lg"
                  }
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {currentPage < pageCount && (
            <PaginationItem>
              <PaginationNext
                href={buildUrl(currentPage + 1)}
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(currentPage + 1);
                }}
                className="border border-brand-light text-brand-navy hover:bg-brand-light/50 hover:text-brand-deep transition-colors rounded-lg"
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default CustomPagination;